import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import FormModal from '../components/FormModal';
import { ActionsSkeletonCell } from '../components/ActionsSkeletonCell';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CheckSquare, Square } from 'lucide-react';
import { categoriesAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useFinancialAccess } from '../hooks/useFinancialAccess';
import { Skeleton } from '../components/Skeleton';

interface StoreCategory {
  id: number;
  category_name: string;
  // null when the caller holds no financial-visibility grant — the server redacts
  // these rather than sending zeros, so they must never be formatted unconditionally.
  payout_min: number | null;
  payout_max: number | null;
  eligibility_criteria?: string;
  seed_level: number;
  min_employees?: number | null;
  status?: string;
  created_at?: string;
  store_count?: number;
}

const currency = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(value);

export default function StoreCategoriesPage({
  embedded, actionSlot,
}: { embedded?: boolean; actionSlot?: HTMLElement | null } = {}) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  // The payout band is what the client pays us per store of this grade — a commercial
  // figure, granted per user in financial_visibility_users. Without the grant the
  // column and the form fields are absent, and the server redacts the values anyway.
  const canSeeMoney = useFinancialAccess(selectedProjectId);

  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingCategory, setEditingCategory] = useState<StoreCategory | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<StoreCategory | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  

  const [formState, setFormState] = useState({
    name: '',
    payoutMin: '',
    payoutMax: '',
    seedLevel: '',
    minEmployees: '',
    criteria: {
      gt2Years: false,
      gt1Year: false,
      gt6Months: false,
    },
  });

  const handleChange = (field: string, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggle = (key: keyof typeof formState.criteria) => {
    setFormState((prev) => ({
      ...prev,
      criteria: { ...prev.criteria, [key]: !prev.criteria[key] },
    }));
  };

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoriesAPI.getAll(selectedProjectId!);
      if (response.data.status === 'success') {
        setCategories(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch categories:', err);
      setError(err.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const resetForm = () => {
    setFormState({
      name: '',
      payoutMin: '',
      payoutMax: '',
      seedLevel: '',
      minEmployees: '',
      criteria: {
        gt2Years: false,
        gt1Year: false,
        gt6Months: false,
      },
    });
  };

  const getEligibilityCriteria = () => {
    const criteria: string[] = [];
    if (formState.criteria.gt2Years) criteria.push('Greater than 2 years experience');
    if (formState.criteria.gt1Year) criteria.push('Greater than 1 year experience');
    if (formState.criteria.gt6Months) criteria.push('Greater than 6 months experience');
    return criteria.length > 0 ? criteria.join(', ') : null;
  };

  const parseEligibilityCriteria = (criteria: string | undefined | null) => {
    const text = criteria || '';
    return {
      gt2Years: text.includes('Greater than 2 years') || text.includes('> 2 years'),
      gt1Year: text.includes('Greater than 1 year') || text.includes('> 1 year'),
      gt6Months: text.includes('Greater than 6 months') || text.includes('> 6 months'),
    };
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    
    // Validation
    if (!formState.name.trim()) {
      setFormError('Category name is required.');
      return;
    }

    const minAmount = Number(formState.payoutMin) || 0;
    const maxAmount = Number(formState.payoutMax) || 0;

    if (minAmount < 0 || maxAmount < 0) {
      setFormError('Payout amounts cannot be negative.');
      return;
    }

    if (minAmount > maxAmount) {
      setFormError('Minimum payout amount cannot be greater than maximum payout amount.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        category_name: formState.name.trim(),
        // Omitted without the grant: the fields were never rendered, so these would be
        // zeros standing in for figures the user cannot see. The server enforces the
        // same rule independently (it preserves the stored band on update).
        ...(canSeeMoney ? { payout_min: minAmount, payout_max: maxAmount } : {}),
        seed_level: formState.seedLevel ? Number(formState.seedLevel) : null,
        min_employees: formState.minEmployees ? Number(formState.minEmployees) : null,
        eligibility_criteria: getEligibilityCriteria(),
        status: 'active',
      };

      if (modalMode === 'create') {
        await categoriesAPI.create({ ...payload, project_id: selectedProjectId });
      } else if (editingCategory) {
        await categoriesAPI.update(String(editingCategory.id), payload);
      }

      setIsModalOpen(false);
      resetForm();
      setFormError(null);
      fetchCategories();
    } catch (err: any) {
      console.error('Failed to save category:', err);
      setFormError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (category: StoreCategory) => {
    try {
      setIsSubmitting(true);
      await categoriesAPI.delete(String(category.id));
      setDeleteConfirm(null);
      fetchCategories();
    } catch (err: any) {
      console.error('Failed to delete category:', err);
      setError(err.response?.data?.message || 'Failed to delete category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setFormError(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const openEditModal = (category: StoreCategory) => {
    setModalMode('edit');
    setEditingCategory(category);
    setFormError(null);
    setFormState({
      name: category.category_name,
      payoutMin: String(category.payout_min || 0),
      payoutMax: String(category.payout_max || 0),
      seedLevel: String(category.seed_level || ''),
      minEmployees: category.min_employees != null ? String(category.min_employees) : '',
      criteria: parseEligibilityCriteria(category.eligibility_criteria),
    });
    setIsModalOpen(true);
  };

  const filteredCategories = categories;

  const addButton = (
    <button
      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
      onClick={openCreateModal}
    >
      Add New Category
    </button>
  );

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Store Categories</h1>
        </div>
      )}

      {/* Embedded, the action lives in the shell's tab row so the two share one
          line. Standalone, it keeps its own right-aligned row. */}
      {actionSlot ? createPortal(addButton, actionSlot) : (
        <div className="flex items-center justify-end gap-4">{addButton}</div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-6 py-4">Category Name</th>
                {canSeeMoney && <th className="whitespace-nowrap px-6 py-4">Payout Range</th>}
                <th className="whitespace-nowrap px-6 py-4">Eligibility Criteria</th>
                <th className="whitespace-nowrap px-6 py-4">Seed Level</th>
                <th className="whitespace-nowrap px-6 py-4">Min Employees</th>
                <th className="whitespace-nowrap px-6 py-4">Created Date</th>
                <th className="sticky right-0 z-10 whitespace-nowrap bg-gray-50/70 px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white text-left">
              {loading ? (
                <>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <tr key={index}>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                      {canSeeMoney && <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-32" /></td>}
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          <Skeleton className="h-5 w-16 rounded-full" />
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-12" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                      <ActionsSkeletonCell />
                    </tr>
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td colSpan={canSeeMoney ? 7 : 6} className="px-6 py-12 text-center">
                    <p className="text-sm text-red-500">{error}</p>
                    <button
                      onClick={fetchCategories}
                      className="mt-2 text-sm text-blue-600 hover:underline"
                    >
                      Try again
                    </button>
                  </td>
                </tr>
              ) : filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan={canSeeMoney ? 7 : 6} className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500">
                      No categories found. Click "Add New Category" to create one.
                    </p>
                  </td>
                </tr>
              ) : (
                filteredCategories.map((category) => (
                  <tr key={category.id} className="group hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{category.category_name}</td>
                    {canSeeMoney && (
                      <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">
                        {category.payout_min != null && category.payout_max != null
                          ? `${currency(category.payout_min)} - ${currency(category.payout_max)}`
                          : '—'}
                      </td>
                    )}
                    <td className="px-6 py-4 text-left">
                      {category.eligibility_criteria ? (
                        <div className="flex flex-wrap gap-1">
                          {category.eligibility_criteria.split(', ').map((criteria, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                            >
                              {criteria
                                .replace('Greater than ', '> ')
                                .replace(' experience', '')}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No criteria set</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{category.seed_level ?? '-'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">
                      {category.min_employees != null ? (
                        category.min_employees
                      ) : (
                        <span className="text-xs text-gray-400">1 (default)</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-600">
                      {category.created_at
                        ? new Date(category.created_at).toLocaleDateString('en-IN')
                        : '-'}
                    </td>
                    <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                      <TableActionGroup>
                        <TableActionButton action="edit" label={`Edit ${category.category_name}`} type="button" onClick={() => openEditModal(category)} />
                        <TableActionButton action="delete" label={`Delete ${category.category_name}`} type="button" onClick={() => {
                            if ((category.store_count ?? 0) > 0) {
                              alert(`Cannot delete "${category.category_name}". It is currently assigned to ${category.store_count} store${category.store_count !== 1 ? 's' : ''}. Please reassign or remove stores from this category first.`);
                            } else {
                              setDeleteConfirm(category);
                            }
                          }} />
                      </TableActionGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={modalMode === 'create' ? 'Add New Store Category' : 'Edit Store Category'}
        size="lg"
        formId="store-category-form"
        isSubmitting={isSubmitting}
        submitLabel={modalMode === 'create' ? 'Add Category' : 'Save Changes'}
      >
        <form id="store-category-form" className="space-y-6" onSubmit={handleSubmit}>
          {formError && <FormErrorBanner message={formError} />}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="category-name">
              Store Category
            </label>
            <input
              id="category-name"
              type="text"
              value={formState.name}
              onChange={(event) => handleChange('name', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="e.g. L0, L1"
              required
            />
          </div>

          {/* Hidden without the grant — and the server leaves the stored band untouched
              on save, so editing a category here cannot blank a figure you can't see. */}
          {canSeeMoney && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Payout Amount Range</p>
            <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="payout-min">
                Minimum Amount (₹)
              </label>
              <input
                id="payout-min"
                type="number"
                min="0"
                value={formState.payoutMin}
                onChange={(event) => handleChange('payoutMin', event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                placeholder="0"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="payout-max">
                Maximum Amount (₹)
              </label>
              <input
                id="payout-max"
                type="number"
                min="0"
                value={formState.payoutMax}
                onChange={(event) => handleChange('payoutMax', event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                placeholder="0"
                required
              />
            </div>
            </div>
          </div>
          )}

          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Eligibility Criteria</p>
            <p className="text-xs text-gray-500">Select which employee experience levels are eligible for this category (optional):</p>
            <div className="space-y-2">
              {[
                { label: 'Greater than 2 years experience', key: 'gt2Years' as const },
                { label: 'Greater than 1 year experience', key: 'gt1Year' as const },
                { label: 'Greater than 6 months experience', key: 'gt6Months' as const },
              ].map((option) => {
                const checked = formState.criteria[option.key];
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => handleToggle(option.key)}
                    className="flex w-full items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:border-blue-200 hover:bg-blue-50/30"
                  >
                    {checked ? <CheckSquare className="h-4 w-4 text-blue-600" /> : <Square className="h-4 w-4 text-gray-400" />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="seed-level">
              SEED Level
            </label>
            <select
              id="seed-level"
              value={formState.seedLevel}
              onChange={(event) => handleChange('seedLevel', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              required
            >
              <option value="">Select SEED Level</option>
              <option value="30">30</option>
              <option value="40">40</option>
              <option value="45">45</option>
              <option value="50">50</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="min-employees">
              Minimum Employees
            </label>
            <input
              id="min-employees"
              type="number"
              min="1"
              step="1"
              value={formState.minEmployees}
              onChange={(event) => handleChange('minEmployees', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="Leave blank for default (1)"
            />
            <p className="text-xs text-gray-500">
              Optional. Stores of this category with fewer employees than this are flagged as vacant
              during redeployment. Leave blank to use the default (1 — vacant only when empty). This
              does not affect store creation or normal employee deployment.
            </p>
          </div>
        </form>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        isSubmitting={isSubmitting}
        title="Delete Category"
        entityLabel="category"
        entityName={deleteConfirm?.category_name}
      />
    </div>
  );
}
